import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import Dropzone from "react-dropzone"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import api from "../api"
import comuni from "../assets/comuni.json"
import { ReactComponent as AddIcon } from "../assets/images/icons/ic-add-circle.svg"
import { ReactComponent as DownloadIcon } from "../assets/images/icons/ic-download.svg"
import { ReactComponent as DeleteIcon } from "../assets/images/icons/ic-delete.svg"
import { ReactComponent as EditIcon } from "../assets/images/icons/ic-image-edit.svg"
import { ReactComponent as RepeatIcon } from "../assets/images/icons/ic-repeat.svg"
import { ReactComponent as CartIcon } from "../assets/images/icons/ic-cart.svg"
import { ReactComponent as BookIcon } from "../assets/images/icons/ic-book-active.svg"
import { ReactComponent as PayPal } from "../assets/images/paypal.svg"
import { ReactComponent as Scalapay } from "../assets/images/scalapay.svg"
import { ReactComponent as Visa } from "../assets/images/visa.svg"
import { ReactComponent as Mastercard } from "../assets/images/mastercard.svg"
import { ReactComponent as Amex } from "../assets/images/amex.svg"
import { ReactComponent as BankIcon } from "../assets/images/icons/ic-bank.svg"
import { ReactComponent as CreditCardIcon } from "../assets/images/icons/ic-card.svg"
import { ReactComponent as BankStripe } from "../assets/images/bank_stripe.svg"
import { ReactComponent as InsertImageIllustration } from "../assets/images/illustrations/il-insert-image.svg"
import UserPlaceholder from "../assets/images/placeholders/user-placeholder.png"
import universities from "../assets/universities.json"
import { AccountStatus, ButtonStatus, CourseType } from "../common/constants"
import { Each } from "../common/Each"
import MainContext from "../common/MainContext"
import Breadcrumb from "../components/Breadcrumb"
import StudentSessions from "../components/StudentSessions"
import Button from "../components/Button"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import UserCourseDialog from "../components/dialogs/UserCourseDialog"
import ImageCropper from "../components/ImageCropper"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import MeatBallsMenu from "../components/MeatBallsMenu"
import Skeleton from "../components/Skeleton"
import StatusBadge from "../components/StatusBadge"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import { DialogStatus } from "../enums"
import typo from "../typography.module.css"
import { formatDateV2, formatPrice, formatTimeV2, getOrderStatusBadge } from "../utils"
import styles from "./Student.module.css"
import Pagination from "../components/Pagination"
import Uploader from "../components/Uploader"
import Select from "../components/Select"

const ProfileField = ({ label, children }) => {
    const Wrapper = children?.type === Select ? 'div' : 'label'
    return <Wrapper className={styles.profileField}><span>{label}</span>{children}</Wrapper>
}

const Student = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const context = useContext(MainContext)
    const { studentId } = useParams()
    const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' }

    const [student, setStudent] = useState(null)
    const [studentLoading, setStudentLoading] = useState(false)
    const [profilePic, setProfilePic] = useState(null)
    const [universitySearch, setUniversitySearch] = useState('')
    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [surname, setSurname] = useState('')
    const [mandatoryProfiler, setMandatoryProfiler] = useState(false)
    const [age, setAge] = useState('')
    const [ageNumeric, setAgeNumeric] = useState(0)
    const [city, setCity] = useState('')
    const [employment, setEmployment] = useState('')
    const [gender, setGender] = useState('')
    const [university, setUniversity] = useState('')
    const [phone, setPhone] = useState('')
    const [error, setError] = useState(null)
    const [loadingCourses, setLoadingCourses] = useState(false)
    const [courses, setCourses] = useState([])
    const [availableCourses, setAvailableCourses] = useState([])
    const [editions, setEditions] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedEdition, setSelectedEdition] = useState(null)
    const [addCourseMode, setAddCourseMode] = useState(false)
    const [editEditionMode, setEditEditionMode] = useState(false)
    const [loadingEditions, setLoadingEditions] = useState(false)
    const [dialogTimeout, setDialogTimeout] = useState(null)
    const [sessions, setSessions] = useState(null)
    const [allSession, setAllSession] = useState(false)
    const [sessionsLoading, setSessionsLoading] = useState(false)
    const [sessionsError, setSessionsError] = useState(false)
    const [orders, setOrders] = useState(null)
    const [showAllOrders, setShowAllOrders] = useState(false)
    const [ordersLoading, setOrdersLoading] = useState(false)
    const ordersRequest = useRef(null)
    const [page, setPage] = useState(0)
    const [per, setPer] = useState(10)
    const [limitModules, setLimitModules] = useState(false)

    const [hasChanges, setHasChanges] = useState(false)
    const [savingStatus, setSavingStatus] = useState(null)
    const [companies, setCompanies] = useState([])
    const [selectedCompany, setSelectedCompany] = useState(null)

    const [dialogOpen, setDialogOpen] = useState(false)
    const [imgFile, setImgFile] = useState(null)

    const [cropMode, setCropMode] = useState(false)
    const [alert, setAlert] = useState({
        open: false,
        title: "",
        message: "",
        status: DialogStatus.Default,
        actions: []
    })

    const [certificates, setCertificates] = useState([])
    const [certDialog, setCertDialog] = useState({ open: false, course: '', edition: '', certificate_id: null, certificate: null })

    const employmentOptions = useMemo(() => [
        {
            id: 0,
            name: t(`profile.employments.student`),
            value: 'student'
        },
        {
            id: 1,
            name: t(`profile.employments.worker`),
            value: 'worker'
        },
        {
            id: 2,
            name: t(`profile.employments.studentWorker`),
            value: 'studentWorker'
        },
        {
            id: 3,
            name: t(`profile.employments.neither`),
            value: 'neither'
        }
    ], [])

    const genders = useMemo(() => [
        {
            id: 0,
            name: "Uomo",
            value: "male"
        },
        {
            id: 1,
            name: "Donna",
            value: "female"
        },
        {
            id: 2,
            name: "Altro/Preferisco non specificare",
            value: "other"
        }
    ], [])

    const resolvedSelectedCompany = useMemo(() => {
        const companyId = selectedCompany?.value ?? student?.company_id ?? student?.company?.id ?? null

        if (companyId === null || companyId === undefined) {
            return null
        }

        return companies.find((option) => String(option.value) === String(companyId)) || {
            id: companyId,
            value: companyId,
            name: selectedCompany?.name ?? student?.company?.name ?? `Azienda ${companyId}`,
        }
    }, [selectedCompany, student, companies])

    const dropzoneRef = useRef(null)

    const getOrders = async () => {
        ordersRequest.current?.abort()
        const controller = new AbortController()
        ordersRequest.current = controller
        setOrdersLoading(true)
        try {
            const orders = await api.get(`/admin/users/${studentId}/orders?page=${page ?? 0}&per=${per ?? 10}&completed_only=${!showAllOrders}`, { signal: controller.signal })
            if (!controller.signal.aborted) setOrders(orders)
        } catch (e) {
            if (!controller.signal.aborted) console.error(e)
        } finally {
            if (!controller.signal.aborted) setOrdersLoading(false)
        }
    }

    const getStudent = async () => {
        setStudentLoading(true)
        try {
            const student = await api.get(`/admin/users/${studentId}`)
            setStudent(student)
            setStudentLoading(false)
            return student
        } catch (e) {
            setStudentLoading(false)
            if (e.code === 3) {
                setError({ code: -1, message: "Studente inesistente" })
            }
            console.error(e)
        }
    }

    const getStudentCertificates = async () => {
        try {
            const certificates = await api.get(`/admin/users/${studentId}/certificates`)
            setCertificates(certificates)
        }
        catch (e) {
            console.error(e)
        }
    }

    const getCompanies = async () => {
        try {
            const response = await api.get(`/admin/companies?page=0&per=500`)
            const mapped = (response?.companies || []).map((company) => ({
                id: company.id,
                value: company.id,
                name: company.name,
            }))
            setCompanies(mapped)
        }
        catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {

        getStudent()
        getStudentCertificates()
        getCompanies()
        getAvailableCourses().then(() => {
            getCourses()
        })

        return () => {
            if (dialogTimeout) {
                clearTimeout(dialogTimeout)
            }
        }
    }, [])

    useEffect(() => {
        if (courses.length === 0)
            return

        const acs = []
        for (const ac of availableCourses) {
            if (['public', 'hidden'].includes(ac.status)) {
                acs.push(ac)
            }
        }
        setAvailableCourses(acs)
    }, [courses])

    const getSessions = async (all = false) => {
        setSessionsLoading(true)
        setSessionsError(false)
        try {
            let sessions = await api.get(`/admin/users/${studentId}/sessions?all=${all}`)
            setSessions(sessions)
            if (all) {
                setAllSession(true)
            }
        }
        catch (e) {
            setSessionsError(true)
            console.error(e)
        } finally {
            setSessionsLoading(false)
        }
    }

    useEffect(() => {

        if (student) {
            setEmail(student.email)
            setName(student.name)
            setSurname(student.surname)
            setEmployment(student.employment)
            setGender(student.gender)
            setUniversity(student.university)
            setCity(student.city)
            setPhone(student.phone ?? '')
            setAgeNumeric(student.age)
            const companyId = student?.company_id ?? student?.company?.id ?? null
            const companyName = companies.find((option) => String(option.value) === String(companyId))?.name ?? student?.company?.name ?? null
            setSelectedCompany(companyId ? { id: companyId, value: companyId, name: companyName || String(companyId) } : null)
        }
    }, [student, companies])

    useEffect(() => {
        if (student) {
            setHasChanges(
                (email !== student.email) ||
                (name !== student.name) ||
                (surname !== student.surname) ||
                (employment !== student.employment) ||
                (gender !== student.gender) ||
                (university !== student.university) ||
                (ageNumeric !== student.age) ||
                (city !== student.city) ||
                (phone !== (student.phone ?? '')) ||
                ((resolvedSelectedCompany?.value ?? null) !== (student?.company_id ?? student?.company?.id ?? null)) ||
                ((profilePic && profilePic !== student.profilePic) ?? false)
            )
        }
    }, [student, email, name, surname, employment, gender, university, ageNumeric, city, phone, profilePic, resolvedSelectedCompany])

    const saveChanges = async () => {
        setSavingStatus(ButtonStatus.Loading)
        try {
            const formData = new FormData()
            formData.append("name", name);
            formData.append("surname", surname);
            if (city) formData.append("city", city);
            if (ageNumeric > 0) formData.append("age", ageNumeric)
            if (gender) formData.append("gender", gender)
            if (university) formData.append("university", university); // TODO verificare con il Team SF se tenerla
            if (employment) formData.append("employment", employment)
            if (phone) formData.append("phone", phone);
            if (email) formData.append("email", email);
            formData.append("company_id", String(selectedCompany?.value ?? -1));

            if (profilePic) {
                let blob = await fetch(profilePic).then(r => r.blob());
                formData.append("picture", blob, `${student.id}-picture.jpg`);
            }

            try {
                const s = await api.put(`/admin/users/${studentId}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                setStudent({
                    ...s,
                    company_id: s?.company_id ?? resolvedSelectedCompany?.value ?? null,
                    company: s?.company ?? (resolvedSelectedCompany ? { id: resolvedSelectedCompany.value, name: resolvedSelectedCompany.name } : null),
                    courses: student.courses,
                })
            } catch (e) {
                throw new Error("Non è stato possibile salvare le modifiche.")
            }

            // setDialogOpen(true)
            setAlert({ open: true, title: t('profile.saved'), message: t('profile.changesSaved'), status: DialogStatus.Success })
            setSavingStatus(ButtonStatus.Success)
        } catch (e) {
            console.error(e)
            setSavingStatus(ButtonStatus.Error)
        }
    }

    const resetAlert = useCallback(() => {
        setAlert({
            open: false,
            title: "",
            message: "",
            status: DialogStatus.Default,
            actions: []
        })
    }, [])

    const changeAccountStatus = useCallback(async (status) => {
        setAlert(a => { return { ...a, status: DialogStatus.Loading } })

        try {
            const student = await api.patch(`/admin/users/${studentId}`, {
                status
            })
            setStudent(student)
            setAlert(a => { return { ...a, status: DialogStatus.Success } })
        } catch (e) {
            console.error(e)
            setAlert(a => { return { ...a, status: DialogStatus.Error } })
        }
    }, [studentId])

    const onAgeChange = (value) => {
        setAge(value)
        const numericAge = parseInt(value)
        if (numericAge < 13) {
            setError({ code: -1, message: t('errors.invalidAge') })
        }
        else {
            setError(null)
        }
        if (!isNaN(numericAge)) {
            setAgeNumeric(numericAge)
        }
    }



    const getAvailableCourses = async () => {
        try {
            const courses = await api.get(`/admin/courses`)
            setAvailableCourses(courses)
        } catch (e) {
            console.error(e)
        }
    }

    const getCourses = async () => {
        setLoadingCourses(true)
        try {
            const courses = await api.get(`/admin/users/${studentId}/courses`)
            setCourses(courses)
        } catch (e) {
            console.error(e)
        }
        setLoadingCourses(false)
    }

    const uploadCertificate = async () => {
        try {
            let cert = await api.post(`/admin/certificates`, {
                certificate_id: certDialog.certificate_id,
                file_url: certDialog.certificate
            })
            getStudentCertificates()
            setCertDialog({ open: false, course: '', edition: '', certificate: null, certificate_id: null })
        }
        catch (e) {
            console.error(e)
        }
    }

    const removeCertificate = async (cert_id) => {
        try {
            await api.delete(`/admin/certificates/${cert_id}`)
            getStudentCertificates()
            setAlert({ open: false, title: "", message: "", status: DialogStatus.Default, actions: [] })
        }
        catch (e) {
            console.error(e)
        }
    }

    const downloadCertificate = (cert) => {
        const certificateUrl = cert?.certificate
        if (!certificateUrl) {
            context.setError({ title: "Attestato non disponibile", message: "Non esiste un file da scaricare per questo attestato." })
            return
        }

        const courseName = (cert?.course?.name || 'attestato').replace(/[^a-zA-Z0-9-_]/g, '_')
        const editionName = (cert?.edition?.name || '').replace(/[^a-zA-Z0-9-_]/g, '_')
        const fileName = `${courseName}${editionName ? `_${editionName}` : ''}.pdf`

        const link = document.createElement('a')
        link.href = certificateUrl
        link.setAttribute('download', fileName)
        link.setAttribute('target', '_blank')
        link.setAttribute('rel', 'noopener noreferrer')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const removeCourse = async (courseId, editionId) => {
        setAlert(a => {
            return {
                ...a,
                status: DialogStatus.Loading,
                actions: a.actions.map(action => ({ ...action, disabled: true }))
            }
        })
        try {
            editionId ?
                await api.delete(`/admin/users/${studentId}/courses/${courseId}/editions/${editionId}`) :
                await api.delete(`/admin/users/${studentId}/courses/${courseId}`)

            setSelectedCourse(null)
            setSelectedEdition(null)
            setEditEditionMode(false)
            setAddCourseMode(false)
            getAvailableCourses().then(r => {
                getCourses()
            })
            getStudent()
            setAlert(a => {
                return {
                    ...a,
                    status: DialogStatus.Success,
                }
            })
            setDialogTimeout(setTimeout(resetAlert, 2000))
        } catch (e) {
            console.error(e)
            setAlert(a => {
                return {
                    ...a,
                    status: DialogStatus.Error,
                    actions: a.actions.map(action => ({ ...action, disabled: false }))
                }
            })
        }
    }

    const [emailStatus, setEmailStatus] = useState(null)
    const getEmailButtonLabel = () => {
        if (emailStatus === 'sending') {
            return 'Invio in corso…'
        }
        if (emailStatus === 'sent') {
            return 'Mail inviata'
        }
        return 'Reinvia email di verifica'
    }

    useEffect(() => {
        if (studentId) {
            getSessions()
        }
    }, [studentId])

    useEffect(() => {
        getOrders()

        return () => ordersRequest.current?.abort()
    }, [page, per, studentId, showAllOrders])

    useEffect(() => {
        if (certificates.length > 0) {
            const queryParams = new URLSearchParams(window.location.search);
            let cert_id = queryParams.get('cert_id')
            if (certificates.find(c => c.id === parseInt(cert_id))) {
                let c = certificates.find(c => c.id === parseInt(cert_id))
                setCertDialog({ open: true, course: c.course.name, edition: c.edition.name, certificate_id: c.id, certificate: null })
            }
        }
    }, [certificates])

    const createSubscriptionOrder = async (user_id, course_id) => {
        try {
            await api.post(`/admin/users/${user_id}/courses/${course_id}/order`)
            await getOrders()
            resetAlert()
        }
        catch (e) {
            console.error(e)
        }
    }

    const getOrderSortDate = (order) => {
        if (!order) return null
        if (order.completed_at) return order.completed_at
        return order.created_at ?? null
    }

    const getOrderStatus = (status) => {
        if (!status) return null
        return getOrderStatusBadge(status)
    }

    const getOrderPaymentMethodLabel = (order) => {
        const paymentMethod = order?.payment_method
        if (!paymentMethod) {
            return "Manuale"
        }

        const methodType = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.type
        if (methodType === "card") {
            return paymentMethod?.card?.brand?.toUpperCase() || "CARD"
        }

        if (methodType) {
            return String(methodType).replace(/_/g, " ").toUpperCase()
        }

        return "-"
    }

    const getOrderPaymentMethodIcon = (order) => {
        const paymentMethod = order?.payment_method
        if (!paymentMethod) {
            return <BankIcon className={styles.paymentMethod} />
        }

        const methodType = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.type

        switch (methodType) {
            case 'paypal':
                return <PayPal className={styles.paymentMethod} />
            case 'scalapay':
                return <Scalapay className={styles.paymentMethod} />
            case 'customer_balance':
                return <BankStripe className={styles.paymentMethod} />
            case 'visa':
                return <Visa className={styles.paymentMethod} />
            case 'mastercard':
                return <Mastercard className={styles.paymentMethod} />
            case 'amex':
                return <Amex className={styles.paymentMethod} />
            case 'card': {
                const brand = paymentMethod?.card?.brand
                if (brand === 'visa') return <Visa className={styles.paymentMethod} />
                if (brand === 'mastercard') return <Mastercard className={styles.paymentMethod} />
                if (brand === 'amex') return <Amex className={styles.paymentMethod} />
                return <CreditCardIcon className={styles.paymentMethod} />
            }
            default:
                return <CreditCardIcon className={styles.paymentMethod} />
        }
    }

    const getMeatballsMenuItems = (course) => {
        let items = [
            {
                id: 1,
                label: course.edition ? "Cambia edizione" : "Aggiungi edizione",
                icon: course.edition ? RepeatIcon : AddIcon,
                style: { color: "var(--sf-yellow)" },
                onClick: () => {
                    setSelectedCourse(course)
                    setEditEditionMode(true)
                    setAddCourseMode(false)
                }
            },
            {
                id: 3,
                label: "Disiscrivi",
                icon: DeleteIcon,
                style: { color: "var(--sf-red)" },
                onClick: () => {
                    setAlert({
                        open: true,
                        title: "Elimina iscrizione",
                        message: `Stai per eliminare l'iscrizione di ${student.name} ${student.surname} al corso "${course.name}${course.edition ? ` - ${course.edition.name}".` : '".'} Sei sicuro di voler procedere?`,
                        actions:
                            [
                                {
                                    label: "Annulla",
                                    onClick: resetAlert,
                                    inverse: true
                                },
                                {
                                    label: "Sì, elimina",
                                    onClick: () => removeCourse(course.id, course.edition?.id ?? null),
                                }
                            ]
                    })

                }
            },
            {
                id: 5,
                label: "Crea ordine",
                icon: CartIcon,
                onClick: () => {
                    setAlert({
                        open: true,
                        title: "Creare ordine?",
                        message: `Stai per creare un nuovo ordine per questa iscrizione. Sei sicuro di voler procedere?`,
                        actions:
                            [
                                {
                                    label: "Annulla",
                                    accentColor: "var(--sf-red)",
                                    onClick: resetAlert,
                                    inverse: true
                                },
                                {
                                    label: "Crea",
                                    accentColor: "var(--primary)",
                                    onClick: async () => {
                                        await createSubscriptionOrder(student.id, course.id)
                                    },
                                }
                            ]
                    })
                }
            }
        ]

        if(course.type === CourseType.Professional && course.edition) {
            items.push(
            {
                id: 2,
                label: "Limita moduli",
                icon: BookIcon,
                style: { color: "var(--secondary)" },
                onClick: () => {
                    setSelectedCourse(course)
                    setLimitModules(true)
                }
            })
        }

        return items
    }

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>{ student ? `${student.name} ${student.surname} | Studente` : "Studente"}</title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <Breadcrumb items={[{ label: "Utenti", to: "/users/students" }, { label: student ? `${student.name} ${student.surname}` : "Profilo utente" }]} />
                        <div className={styles.content}>
                            <div className={styles.leftColumn}>
                                <div className={styles.student}>
                                    <div className={styles.studentTitle}>
                                        <div className={styles.profileHeading}>
                                            <div className={styles.username}>{`${student?.name ?? ""} ${student?.surname ?? ""}`}</div>
                                            <MeatBallsMenu actions={student?.status === AccountStatus.Active ? [{ label: 'Sospendi utente', onClick: () => {
                                                        setAlert({
                                                            open: true,
                                                            title: "Sospensione studente",
                                                            message: "Lo studente non potrà più accedere alla piattaforma studente. Sei sicuro di voler procedere?",
                                                            status: DialogStatus.Default,
                                                            actions: [
                                                                {
                                                                    label: "Annulla",
                                                                    onClick: resetAlert,
                                                                    inverse: true
                                                                },
                                                                {
                                                                    label: "Sì, sospendi",
                                                                    onClick: () => changeAccountStatus(AccountStatus.Suspended),
                                                                }
                                                            ]
                                                        }) } }] : student?.status === AccountStatus.Suspended ? [{ label: 'Riattiva utente', onClick: () => {
                                                        setAlert({
                                                            open: true,
                                                            title: "Attivazione studente",
                                                            message: "Lo studente potrà accedere alla piattaforma studenti. Sei sicuro di voler procedere?",
                                                            status: DialogStatus.Default,
                                                            actions: [
                                                                {
                                                                    label: "Annulla",
                                                                    onClick: resetAlert,
                                                                    inverse: true
                                                                },
                                                                {
                                                                    label: "Sì, attiva",
                                                                    onClick: () => changeAccountStatus(AccountStatus.Active),
                                                                }
                                                            ]
                                                        }) } }] : []} />
                                        </div>
                                        <div className={styles.badges}>
                                            <StatusBadge appearance="soft" label={student?.email_verified ? 'VERIFICATO' : 'NON VERIFICATO'} color={student?.email_verified ? 'var(--sf-green)' : 'var(--sf-red)'} />
                                            {student?.locked && <StatusBadge appearance="soft" label="BLOCCATO" color="gray" />}
                                            {!student?.email_verified && Boolean(student?.registered_at) &&
                                                <Button appearance="text" disabled={['sending', 'sent'].includes(emailStatus)} style={{ padding: 0 }} onClick={async () => {
                                                    setEmailStatus('sending')
                                                    try {
                                                        await api.get(`/verify-email?email=${student?.email}`)
                                                        setEmailStatus('sent')
                                                        setTimeout(() => {
                                                            setEmailStatus(null)
                                                        }, 10000)
                                                    }
                                                    catch (e) {
                                                        console.error(e)
                                                        context.setError({ title: "Impossibile inviare la mail di conferma.", message: e.message })
                                                        setEmailStatus(null)
                                                    }
                                                }}>{getEmailButtonLabel()}</Button>
                                            }
                                        </div>
                                    </div>
                                    <div className={styles.profilePicContainer}>
                                        <img className={styles.profilePic} src={(profilePic || student?.picture) ?? UserPlaceholder} alt='' />
                                        <div className={styles.profilePicOverlay} onClick={() => {
                                            setDialogOpen(true)
                                        }}>
                                            <EditIcon />
                                        </div>
                                    </div>
                                    {
                                        studentLoading &&
                                        <Skeleton type="rect" width="164px" height="24px" borderRadius={"12px"} />
                                    }
                                    {
                                        !studentLoading &&
                                        <div className={styles.bio}>
                                            <div className={styles.email}>{student?.email}</div>
                                            <div className={styles.bioDate}>
                                                <div className={typo.headline}>CREAZIONE UTENZA</div>
                                                <div>
                                                    {formatDateV2(student?.created_at, dateOptions, 'it', true, true)}, {formatTimeV2(student?.created_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                </div>
                                            </div>
                                            <div className={styles.bioDate}>
                                                <div className={typo.headline}>REGISTRATO IL</div>
                                                {student?.registered_at &&
                                                    <div>
                                                        {formatDateV2(student?.registered_at, dateOptions, 'it', true, true)}, {formatTimeV2(student?.registered_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                                    </div>
                                                }
                                                {!student?.registered_at &&
                                                    <div>Non registrato</div>
                                                }
                                            </div>
                                            {sessions && sessions.length > 0 &&
                                                <div className={styles.bioDate}>
                                                    <div className={typo.headline}>ULTIMO ACCESSO</div>
                                                    <div>{formatDateV2(sessions[0].started_at, dateOptions, 'it', true, true)}, {formatTimeV2(sessions[0].started_at, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                                                </div>
                                            }
                                        </div>


                                    }
                                </div>
                            </div>
                            <div className={styles.rightColumn}>
                                <Card appearance="modern" title="ANAGRAFICA">
                                    <div className={styles.card}>
                                        <div className={styles.inputs}>
                                            <div className={styles.row}>
                                                <ProfileField label="Nome"><TextInput value={name} type="text" placeholder="Nome" onKeyUp={setName} /></ProfileField>
                                                <ProfileField label="Cognome"><TextInput value={surname} type="text" placeholder="Cognome" onKeyUp={setSurname} /></ProfileField>
                                            </div>
                                            <ProfileField label="Email"><TextInput value={email} type="text" placeholder="nome@esempio.it" onKeyUp={setEmail} /></ProfileField>
                                            <div className={styles.row}>
                                                <ProfileField label="Occupazione"><Select appearance="modern" options={employmentOptions} selected={employmentOptions.find(e => e.value === employment)} placeholder="Seleziona occupazione" onSelect={option => setEmployment(option?.value)} nullable /></ProfileField>
                                                <ProfileField label="Genere"><Select appearance="modern" options={genders} selected={genders.find(g => g.value === gender)} placeholder="Seleziona genere" onSelect={option => setGender(option?.value)} nullable /></ProfileField>
                                            </div>
                                            {["student", "studentWorker"].includes(employment) &&
                                                <ProfileField label="Università"><Select appearance="modern" options={universities.map((u, id) => ({ id, name: u.name })).filter(u => u.name.toLowerCase().includes(universitySearch.toLowerCase()))} selected={university ? { id: universities.findIndex(u => u.name === university), name: university } : null} placeholder="Seleziona università" onSelect={option => setUniversity(option?.id === -1 ? '' : option?.name || '')} nullable onSearch={query => setUniversitySearch(query)} /></ProfileField>
                                            }
                                            <div className={styles.row}>
                                                <ProfileField label="Età"><TextInput value={ageNumeric} type="number" placeholder="Età" onKeyUp={onAgeChange} /></ProfileField>
                                                <ProfileField label="Città"><TextInput value={city} type="text" placeholder="Città (provincia)" onKeyUp={setCity} autocomplete="off" suggestions={comuni.map(c => `${c.nome} (${c.sigla})`)} /></ProfileField>
                                            </div>
                                            <div className={styles.row}>
                                                <ProfileField label="Telefono"><TextInput value={student?.phone} disabled type="text" placeholder="Non disponibile" /><small>Il numero di telefono non è modificabile da questa pagina.</small></ProfileField>
                                                <ProfileField label="Azienda"><Select appearance="modern" options={companies} selected={resolvedSelectedCompany} placeholder="Seleziona azienda" onSelect={option => setSelectedCompany(option ?? null)} nullable nullLabel="Nessuna azienda" /></ProfileField>
                                            </div>
                                            <div className={styles.error}>
                                                {error && error.code === -1 &&
                                                    <span className={typo.caption}>
                                                        {error.message}
                                                    </span>
                                                }
                                            </div>
                                        </div>
                                        <div className={styles.studentActions}>
                                            <Button
                                                status={savingStatus}
                                                accentColor="var(--primary)"
                                                additionalClass={styles.saveChangesButton}
                                                disabled={hasChanges === false}
                                                onClick={saveChanges}
                                            >
                                                {t("profile.saveChanges").charAt(0).toUpperCase() + t("profile.saveChanges").slice(1).toLowerCase()}
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            </div>
                        </div>

                        <Card appearance="modern" title={`Iscrizioni ai corsi · ${student?.courses?.length || 0}`} actions={<Button
                                onClick={() => {
                                    if (!addCourseMode) {
                                        setAddCourseMode(true)
                                        setEditEditionMode(false)
                                        setEditions([])
                                        setSelectedCourse(null)
                                        setSelectedEdition(null)
                                    } else {
                                        setAddCourseMode(false)
                                    }
                                }}
                                appearance="subtle"
                                disabled={loadingCourses}
                            >
                                {!addCourseMode ? "Iscrivi" : "Annulla"}
                            </Button>}>
                            <div className={styles.coursesCard}>
                                {student && student.courses?.length > 0 &&
                                    <Each of={student.courses} render={(course) => {
                                        return (
                                            <article className={styles.enrollment}>
                                                <Link className={styles.enrollmentLink} to={`/courses/${course.id}`}>
                                                <img src={course.thumbnail} className={styles.thumbnail} alt="" />
                                                <div className={styles.info}>

                                                    <div className={styles.title}>{course.name}</div>
                                                    <span className={styles.editionLabel}>{course.edition?.name || "Nessuna edizione associata"}</span>
                                                    {course.access_mode === 'limited' && <span className={styles.limitedAccess}>
                                                        Accesso limitato{course.source_quiz_attempt_id ? ' · Quiz' : ''}
                                                        {Array.isArray(course.allowed_lesson_ids) && ` · ${course.allowed_lesson_ids.length} ${course.allowed_lesson_ids.length === 1 ? 'lezione sbloccata' : 'lezioni sbloccate'}`}
                                                    </span>}
                                                    {
                                                        course.access_mode !== 'limited' && (course.edition && course.edition.limit_modules && course.edition.limit_modules.length > 0 ? `${course.edition.limit_modules.length} moduli` : (course.type === CourseType.Professional ? 'Tutti i moduli' : null))
                                                    }
                                                </div>
                                                </Link>
                                                <div className={styles.enrollmentActions}><MeatBallsMenu actions={getMeatballsMenuItems(course)} /></div>
                                            </article>
                                        )
                                    }} />
                                }
                                {
                                    !loadingCourses && student?.courses?.length === 0 &&
                                    <div className={styles.noData}> Nessun Corso </div>
                                }
                            </div>
                        </Card>

                        <Card appearance="modern" title={`Attestati · ${certificates?.length || 0}`}>
                            <div className={styles.certificates}>
                                {certificates?.length === 0 && <div className={styles.noData}>Nessun attestato disponibile per questo utente.</div>}
                                <Each of={certificates} render={(cert) => {
                                    let actions = [
                                        {
                                            id: 1,
                                            label: "Carica attestato",
                                            icon: AddIcon,
                                            style: { color: "var(--sf-yellow)" },
                                            onClick: () => {
                                                setCertDialog({ open: true, course: cert.course.name, edition: cert.edition ? cert.edition.name : '', certificate: null, certificate_id: cert.id })
                                            }
                                        }

                                    ]

                                    if (cert.certificate) {
                                        actions = [...actions, {
                                            id: 2,
                                            label: "Scarica attestato",
                                            icon: DownloadIcon,
                                            style: { color: "var(--sf-green)" },
                                            onClick: () => {
                                                downloadCertificate(cert)
                                            }
                                        },
                                        {
                                            id: 3,
                                            label: "Rimuovi",
                                            icon: DeleteIcon,
                                            style: { color: "var(--sf-red)" },
                                            onClick: () => {
                                                setAlert({
                                                    open: true,
                                                    title: "Rimuovere l'attestato",
                                                    message: `Stai per eliminare l'attestato di ${student.name} ${student.surname} per il corso "${cert.course.name}${cert.edition ? ` - ${cert.edition.name}".` : '".'} Sei sicuro di voler procedere?`,
                                                    actions:
                                                        [
                                                            {
                                                                label: "Annulla",
                                                                onClick: resetAlert,
                                                                inverse: true
                                                            },
                                                            {
                                                                label: "Sì, elimina",
                                                                onClick: () => { removeCertificate(cert.id) }
                                                            }
                                                        ]
                                                })

                                            }
                                        }]
                                    }

                                    return (
                                        <article className={styles.certificateCard}>
                                            <div className={styles.certificateHeader}>
                                                {cert.course.thumbnail ? <img src={cert.course.thumbnail} className={styles.certificateThumbnail} alt="" /> : <div className={styles.certificatePlaceholder}><BookIcon aria-hidden="true" /></div>}
                                                <div className={styles.certificateInfo}>
                                                    <h3>{cert.course.name}</h3>
                                                    {cert.edition?.name && <span className={styles.editionLabel}>{cert.edition.name}</span>}
                                                </div>
                                                <MeatBallsMenu actions={actions} />
                                            </div>
                                            <div className={styles.certificateMeta}>
                                                <StatusBadge appearance="soft" label={cert.certificate ? "Disponibile" : "Da caricare"} color={cert.certificate ? "var(--sf-green)" : "var(--sf-gray)"} />
                                                {(cert.certificate ? cert.updated_at : cert.created_at) && <span className={styles.certDate}>
                                                    {cert.certificate ? 'Aggiornato il ' : 'Creato il '}{formatDateV2(cert.certificate ? cert.updated_at : cert.created_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                                                </span>}
                                            </div>
                                            <div className={styles.certificateFooter}>
                                                <span>{cert.certificate ? 'Attestato pronto per il download' : 'Nessun documento caricato'}</span>
                                                <Button appearance="subtle" onClick={() => cert.certificate ? downloadCertificate(cert) : actions[0].onClick()}>
                                                    {cert.certificate ? <DownloadIcon aria-hidden="true" /> : <AddIcon aria-hidden="true" />}
                                                    {cert.certificate ? 'Scarica' : 'Carica attestato'}
                                                </Button>
                                            </div>
                                        </article>
                                    )
                                }} />
                            </div>
                        </Card>

                        <Card appearance="modern" title={showAllOrders ? "Tutti gli ordini" : "Ordini completati"} actions={
                            <Button appearance="subtle" aria-pressed={showAllOrders} onClick={() => { setPage(0); setOrders(null); setShowAllOrders(value => !value) }}>
                                {showAllOrders ? 'Mostra solo completati' : 'Mostra tutti'}
                            </Button>
                        }>
                            <div className={styles.orders}>
                                <Table
                                    appearance="minimal"
                                    loading={ordersLoading}
                                    defaultSort={{ key: 'date', direction: 'desc' }}
                                    columns={[
                                        { key: 'date', label: 'DATA', type: 'datetime' },
                                        { key: 'order_no', label: 'ID ORDINE', type: 'string' },
                                        { key: 'products', label: 'PRODOTTI', type: 'custom' },
                                        { key: 'amount', label: 'IMPORTO', type: 'custom' },
                                        { key: 'payment_method', label: 'METODO PAGAMENTO', type: 'custom' },
                                        { key: 'status', label: 'STATO', type: 'custom' },
                                    ]}
                                    elements={(orders?.orders || []).map((order) => ({
                                        ...order,
                                        date: getOrderSortDate(order),
                                        order_no: order?.order_no ? `#${order.order_no}` : '-',
                                        products: {
                                            value: order?.products?.length ?? 0,
                                            type: 'number',
                                            component: (
                                                <span title={(order?.products || []).map((p) => p?.name).filter(Boolean).join(', ')}>
                                                    {(order?.products || []).map(product => product.name).filter(Boolean).join(', ') || '—'}
                                                </span>
                                            ),
                                        },
                                        amount: {
                                            value: order?.amount ?? 0,
                                            type: 'number',
                                            component: <span>€ {formatPrice((order?.amount ?? 0) / 100)}</span>,
                                        },
                                        payment_method: {
                                            value: getOrderPaymentMethodLabel(order),
                                            type: 'string',
                                            component: (
                                                <span title={getOrderPaymentMethodLabel(order)}>
                                                    {getOrderPaymentMethodIcon(order)}
                                                </span>
                                            ),
                                        },
                                        status: {
                                            value: order?.status ?? '',
                                            type: 'string',
                                            component: (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    {order?.test && <StatusBadge appearance="soft" label="Test" color="var(--sf-orange)" />}
                                                    {getOrderStatus(order?.status) && <StatusBadge appearance="soft" {...getOrderStatus(order?.status)} />}
                                                </div>
                                            ),
                                        },
                                        href: `/orders/${order.id}`,
                                    }))}
                                    onRowClick={(item) => {
                                        navigate(`/orders/${item.id}`)
                                    }}
                                />

                                {!ordersLoading && orders?.orders?.length === 0 &&
                                    <div className={styles.noData}>{showAllOrders ? "Nessun ordine" : "Nessun ordine completato"}</div>
                                }
                            </div>
                            {orders && orders.orders.length > 0 &&
                                <Pagination total={orders?.count ?? 0} per={per} page={page} onChange={p => setPage(p)} />
                            }
                        </Card>

                        <Card appearance="modern" title="Attività e sessioni" actions={
                            !allSession && <Button appearance="subtle" disabled={sessionsLoading} onClick={() => getSessions(true)}>{sessionsLoading ? 'Caricamento…' : 'Vedi tutto lo storico'}</Button>
                        }>
                            <StudentSessions sessions={sessions} all={allSession} loading={sessionsLoading} error={sessionsError} onRetry={() => getSessions(allSession)} />
                        </Card>
                        <Dialog
                            open={dialogOpen}
                            style={{
                                width: "30vw",
                                maxWidth: "512px"
                            }}
                            onClose={() => {
                                setDialogOpen(false)
                                setCropMode(false)
                            }}
                            title={"Immagine del profilo"}>
                            <div className={styles.dialog}>
                                {!cropMode &&
                                    <Dropzone
                                        onDrop={(files) => {
                                            if (files[0]) {
                                                setImgFile(files[0])
                                                setCropMode(true)
                                            }
                                        }}
                                        accept={{
                                            'image/jpeg': [],
                                            'image/png': [],
                                            'image/heic': []
                                        }}
                                    >
                                        {({ getRootProps, getInputProps }) => (
                                            <section style={{ display: 'flex', width: '100%' }}>
                                                <div {...getRootProps()} style={{ display: 'flex', width: '100%' }}>
                                                    <input {...getInputProps()} ref={dropzoneRef} />
                                                    <div className={styles.editPictureDropzone}>
                                                        <InsertImageIllustration></InsertImageIllustration>
                                                        <div className={styles.editPictureLabel}>
                                                            {t('profile.dropImage')}
                                                        </div>
                                                        <Button
                                                            style={{ marginTop: '.5rem', padding: '0.6rem 2rem' }}
                                                            accentColor={"var(--tertiary)"}
                                                            onClick={() => dropzoneRef.current?.click()}
                                                        >
                                                            {t('profile.chooseImage').charAt(0).toUpperCase() + t('profile.chooseImage').slice(1).toLowerCase()}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </section>
                                        )}
                                    </Dropzone>
                                }
                                {cropMode &&
                                    <>
                                        <ImageCropper
                                            src={URL.createObjectURL(imgFile)}
                                            onSave={(image) => {
                                                setProfilePic(image)
                                                setCropMode(false)
                                                setDialogOpen(false)
                                            }}
                                            onCancel={() => { setCropMode(false) }}
                                            compression={{ quality: 0.7, maxWidth: 300, maxHeight: 300 }}
                                        >
                                        </ImageCropper>
                                    </>
                                }
                            </div>
                        </Dialog>

                        <Dialog title={"Carica attestato"} open={certDialog.open} onClose={() => { setCertDialog({ open: false, course: '', edition: '', certificate: null }) }} action={
                            <Button onClick={uploadCertificate} style={{ padding: '1rem 2rem', fontSize: '1rem' }} disabled={!certDialog.certificate} accentColor={"var(--primary)"}>Carica</Button>
                        }>
                            <div className={styles.dialog}>
                                <div className={typo.body}>
                                    Carica l'attestato per <b>{student?.name} {student?.surname}</b> per il corso di <b>{certDialog.course} - {certDialog.edition}</b>
                                </div>
                                <div className={styles.uploader}>
                                    <Uploader onComplete={(files) => {
                                        setCertDialog(prev => ({ ...prev, certificate: files[0].url }))
                                    }} />
                                </div>
                            </div>
                        </Dialog>

                        <AlertDialog
                            open={alert.open}
                            title={alert.title}
                            text={alert.message}
                            onClose={alert.onClose ?? resetAlert}
                            actions={alert.actions}
                            status={alert.status}
                        />

                        {student && courses && (addCourseMode || editEditionMode || limitModules) &&
                            <UserCourseDialog
                                user={student}
                                courses={availableCourses}
                                course_id={editEditionMode || limitModules ? selectedCourse?.id : null}
                                edition_id={editEditionMode || limitModules ? selectedCourse?.edition?.id : null}
                                limitModules={limitModules}
                                limited_modules={limitModules ? selectedCourse?.edition?.limit_modules : null}
                                onClose={() => {
                                    setLimitModules(false)
                                    setAddCourseMode(false)
                                    setEditEditionMode(false)
                                    getStudent()
                                    getOrders()
                                }} />
                        }
                    </div>
                </div>
            </div >
        </HeaderFooterLayout >
    )
}

export default Student
